export const AI_EXHIBIT_BATCH5_COPY = Object.freeze({
  en: {
    family: {
      eyebrow: "THE AI FAMILY",
      title: "AI terms overlap, but they are not synonyms.",
      subtitle:
        "Tap a term to see how it relates to the others. The goal is a useful map—not a rigid taxonomy.",
      terms: [
        {
          id: "ai",
          title: "Artificial Intelligence",
          icon: "🧠",
          short: "The broad umbrella",
          description:
            "Artificial intelligence is the broad field of building computer systems that perform tasks involving capabilities such as perception, prediction, language, planning, reasoning, or decision-making.",
          relation:
            "AI includes many historical and modern approaches. Not all AI is machine learning.",
          examples: ["Rule-based expert systems", "Search and planning", "Machine-learning systems"],
        },
        {
          id: "ml",
          title: "Machine Learning",
          icon: "📈",
          short: "Learning patterns from data",
          description:
            "Machine learning is a major approach within AI in which a model learns useful patterns from examples or experience rather than relying only on hand-written rules.",
          relation:
            "Machine learning is part of AI, but AI historically also includes approaches that are not machine learning.",
          examples: ["Spam filtering", "Recommendations", "Image classification"],
        },
        {
          id: "deep",
          title: "Deep Learning",
          icon: "🔗",
          short: "Machine learning with multilayer neural networks",
          description:
            "Deep learning is a form of machine learning that uses neural networks with multiple layers to learn complex representations from data.",
          relation:
            "Deep learning is part of machine learning. Many—but not all—modern AI systems use deep learning.",
          examples: ["Speech recognition", "Computer vision", "Large language models"],
        },
        {
          id: "gen",
          title: "Generative AI",
          icon: "✨",
          short: "AI that generates new content",
          description:
            "Generative AI describes systems designed to generate new text, images, audio, video, code, or other content.",
          relation:
            "Generative AI describes what the system does. Many modern generative systems use deep learning, but the term is not the name of one specific architecture.",
          examples: ["Text generation", "Image generation", "Music and code generation"],
        },
      ],
      mapTitle: "A useful mental map",
      mapLines: [
        "Artificial Intelligence is the broadest umbrella.",
        "Machine Learning is one major approach within AI.",
        "Deep Learning is a kind of machine learning.",
        "Generative AI describes a capability: generating content.",
      ],
      caution:
        "These categories can overlap. Real systems often combine several techniques, so clean textbook boxes do not capture every implementation.",
    },

    myths: {
      eyebrow: "AI MYTHS",
      title: "Some familiar claims sound simple—but the reality is more nuanced.",
      subtitle:
        "Tap a claim to reveal what is established, what is uncertain, and what the exhibit avoids overstating.",
      myths: [
        {
          id: "robot",
          claim: "AI = robot",
          verdict: "No.",
          explanation:
            "Robotics and AI overlap, but they are not the same thing. A robot can follow fixed programmed motions without AI, and AI software can exist with no robot at all.",
        },
        {
          id: "search",
          claim: "AI just searches the internet",
          verdict: "No.",
          explanation:
            "Some AI products use search or retrieval tools, but trained models can also generate outputs from learned internal representations. A modern assistant may combine both.",
        },
        {
          id: "understands",
          claim: "If AI sounds human, it must understand exactly like a human",
          verdict: "Not established.",
          explanation:
            "Human-like language does not by itself prove that a system has human-like internal understanding, experience, or awareness. Behavior and internal mechanisms are different questions.",
        },
        {
          id: "conscious",
          claim: "Today's mainstream AI systems are conscious",
          verdict: "There is no established scientific evidence for that claim.",
          explanation:
            "Current systems can produce strikingly human-like language and behavior, but that is not the same as demonstrating subjective experience or consciousness.",
        },
        {
          id: "objective",
          claim: "AI is always objective",
          verdict: "No.",
          explanation:
            "AI systems reflect choices about data, labels, objectives, evaluation, deployment, and institutions. Bias can enter through many parts of the system.",
        },
        {
          id: "correct",
          claim: "If AI is confident, it is probably correct",
          verdict: "No.",
          explanation:
            "Generated confidence, fluent wording, or a high model score is not proof of truth. Important outputs still need appropriate evidence and verification.",
        },
      ],
      takeaway:
        "Good AI literacy means resisting both hype and dismissal. The interesting questions usually live in the details.",
    },

    sources: {
      eyebrow: "SOURCES & LEARN MORE",
      title: "Museum claims should be traceable.",
      subtitle:
        "These are the main sources behind the exhibit's definitions, history, current capability claims, risk framing, and environmental data.",
      cards: [
        {
          id: "nist",
          title: "NIST AI Risk Management Framework",
          organization: "U.S. National Institute of Standards and Technology",
          url: "https://www.nist.gov/itl/ai-risk-management-framework",
          usedFor:
            "Trustworthy AI, risk management, reliability, safety, transparency, privacy, fairness, and societal impact framing.",
        },
        {
          id: "nistgen",
          title: "NIST Generative AI Profile",
          organization: "U.S. National Institute of Standards and Technology",
          url: "https://www.nist.gov/publications/artificial-intelligence-risk-management-framework-generative-artificial-intelligence",
          usedFor:
            "Generative-AI-specific risk framing and current responsible-AI guidance.",
        },
        {
          id: "stanford",
          title: "2026 AI Index Report",
          organization: "Stanford Institute for Human-Centered AI",
          url: "https://hai.stanford.edu/ai-index/2026-ai-index-report",
          usedFor:
            "Current AI capability, agents, robotics, responsible AI, research, industry trends, and technical-performance benchmarks.",
        },
        {
          id: "dartmouth",
          title: "Dartmouth AI History",
          organization: "Dartmouth College",
          url: "https://ai.dartmouth.edu/our-story",
          usedFor:
            "The 1956 Dartmouth Summer Research Project and the early history of the term artificial intelligence.",
        },
        {
          id: "deepblue",
          title: "IBM Deep Blue",
          organization: "IBM",
          url: "https://www.ibm.com/history/deep-blue",
          usedFor:
            "The 1997 Kasparov match and Deep Blue's place in AI history.",
        },
        {
          id: "alexnet",
          title: "ImageNet Classification with Deep Convolutional Neural Networks",
          organization: "Krizhevsky, Sutskever & Hinton · 2012",
          url: "https://proceedings.neurips.cc/paper/2012/hash/c399862d3b9d6b76c8436e924a68c45b-Abstract.html",
          usedFor:
            "The 2012 deep-learning/ImageNet milestone and GPU-enabled neural-network training.",
        },
        {
          id: "transformer",
          title: "Attention Is All You Need",
          organization: "Vaswani et al. · 2017",
          url: "https://arxiv.org/abs/1706.03762",
          usedFor:
            "The Transformer architecture and its role in later language models.",
        },
        {
          id: "iea",
          title: "Energy and AI",
          organization: "International Energy Agency",
          url: "https://www.iea.org/reports/energy-and-ai",
          usedFor:
            "Data-center electricity demand, infrastructure, energy, and environmental context.",
        },
      ],
      metadataTitle: "How we manage exhibit claims",
      metadata:
        "Production content should track: claim · source · publication date · last reviewed · curator · English copy · Spanish copy.",
      reviewed:
        "The current-status module is intentionally separate so benchmark and energy numbers can be reviewed and updated without redesigning the exhibit.",
      linkNote:
        "In kiosk mode, these links may be represented by QR codes or a take-home resource page rather than opening an unrestricted browser.",
    },
  },

  es: {
    family: {
      eyebrow: "LA FAMILIA DE LA IA",
      title: "Los términos de IA se superponen, pero no son sinónimos.",
      subtitle:
        "Toca un término para ver cómo se relaciona con los demás. El objetivo es un mapa útil, no una taxonomía rígida.",
      terms: [
        {
          id: "ai",
          title: "Inteligencia Artificial",
          icon: "🧠",
          short: "El gran paraguas",
          description:
            "La inteligencia artificial es el campo amplio de sistemas informáticos diseñados para realizar tareas relacionadas con percepción, predicción, lenguaje, planificación, razonamiento o toma de decisiones.",
          relation:
            "La IA incluye muchos enfoques históricos y modernos. No toda IA es aprendizaje automático.",
          examples: ["Sistemas expertos basados en reglas", "Búsqueda y planificación", "Sistemas de aprendizaje automático"],
        },
        {
          id: "ml",
          title: "Aprendizaje Automático",
          icon: "📈",
          short: "Aprender patrones de los datos",
          description:
            "El aprendizaje automático es un enfoque importante dentro de la IA en el que un modelo aprende patrones útiles a partir de ejemplos o experiencia.",
          relation:
            "El aprendizaje automático es parte de la IA, pero históricamente la IA también incluye enfoques que no son aprendizaje automático.",
          examples: ["Filtro de spam", "Recomendaciones", "Clasificación de imágenes"],
        },
        {
          id: "deep",
          title: "Aprendizaje Profundo",
          icon: "🔗",
          short: "Aprendizaje automático con redes neuronales multicapa",
          description:
            "El aprendizaje profundo es una forma de aprendizaje automático que utiliza redes neuronales con múltiples capas.",
          relation:
            "El aprendizaje profundo es parte del aprendizaje automático. Muchos sistemas modernos lo usan, pero no todos.",
          examples: ["Reconocimiento de voz", "Visión por computador", "Grandes modelos de lenguaje"],
        },
        {
          id: "gen",
          title: "IA Generativa",
          icon: "✨",
          short: "IA que genera contenido nuevo",
          description:
            "La IA generativa describe sistemas diseñados para generar texto, imágenes, audio, video, código u otro contenido.",
          relation:
            "IA generativa describe lo que hace el sistema. Muchos sistemas modernos usan aprendizaje profundo, pero el término no nombra una sola arquitectura.",
          examples: ["Generación de texto", "Generación de imágenes", "Música y código"],
        },
      ],
      mapTitle: "Un mapa mental útil",
      mapLines: [
        "Inteligencia Artificial es el paraguas más amplio.",
        "Aprendizaje Automático es un enfoque importante dentro de la IA.",
        "Aprendizaje Profundo es un tipo de aprendizaje automático.",
        "IA Generativa describe una capacidad: generar contenido.",
      ],
      caution:
        "Estas categorías pueden superponerse. Los sistemas reales suelen combinar varias técnicas.",
    },

    myths: {
      eyebrow: "MITOS SOBRE LA IA",
      title: "Algunas afirmaciones parecen simples, pero la realidad tiene más matices.",
      subtitle:
        "Toca una afirmación para ver qué está establecido, qué es incierto y qué evita exagerar la exhibición.",
      myths: [
        { id: "robot", claim: "IA = robot", verdict: "No.", explanation: "Robótica e IA se superponen, pero no son lo mismo. Un robot puede seguir movimientos programados sin IA y puede existir IA sin ningún robot." },
        { id: "search", claim: "La IA solo busca en internet", verdict: "No.", explanation: "Algunos productos usan búsqueda o recuperación, pero los modelos entrenados también pueden generar a partir de representaciones aprendidas. Un asistente moderno puede combinar ambas cosas." },
        { id: "understands", claim: "Si la IA suena humana, entiende exactamente como un humano", verdict: "No está establecido.", explanation: "El lenguaje humano no demuestra por sí mismo comprensión, experiencia o conciencia humanas." },
        { id: "conscious", claim: "Los sistemas actuales de IA son conscientes", verdict: "No existe evidencia científica establecida para esa afirmación.", explanation: "Los sistemas actuales pueden producir lenguaje sorprendentemente humano, pero eso no demuestra experiencia subjetiva." },
        { id: "objective", claim: "La IA siempre es objetiva", verdict: "No.", explanation: "Los sistemas reflejan decisiones sobre datos, etiquetas, objetivos, evaluación, despliegue e instituciones." },
        { id: "correct", claim: "Si la IA suena segura, probablemente tiene razón", verdict: "No.", explanation: "La confianza, fluidez o una puntuación alta no prueban la verdad. Los resultados importantes todavía necesitan evidencia y verificación." },
      ],
      takeaway:
        "Una buena alfabetización en IA evita tanto la exageración como el rechazo simplista. Las preguntas interesantes suelen estar en los detalles.",
    },

    sources: {
      eyebrow: "FUENTES Y MÁS INFORMACIÓN",
      title: "Las afirmaciones del museo deben poder rastrearse.",
      subtitle:
        "Estas son las principales fuentes detrás de definiciones, historia, capacidades actuales, riesgos e impacto ambiental.",
      cards: [
        { id: "nist", title: "NIST AI Risk Management Framework", organization: "U.S. National Institute of Standards and Technology", url: "https://www.nist.gov/itl/ai-risk-management-framework", usedFor: "IA confiable, riesgo, seguridad, transparencia, privacidad, equidad e impacto social." },
        { id: "nistgen", title: "NIST Generative AI Profile", organization: "U.S. National Institute of Standards and Technology", url: "https://www.nist.gov/publications/artificial-intelligence-risk-management-framework-generative-artificial-intelligence", usedFor: "Riesgos específicos de IA generativa y orientación responsable." },
        { id: "stanford", title: "2026 AI Index Report", organization: "Stanford Institute for Human-Centered AI", url: "https://hai.stanford.edu/ai-index/2026-ai-index-report", usedFor: "Capacidades actuales, agentes, robótica, IA responsable, investigación e indicadores técnicos." },
        { id: "dartmouth", title: "Dartmouth AI History", organization: "Dartmouth College", url: "https://ai.dartmouth.edu/our-story", usedFor: "El proyecto de Dartmouth de 1956 y la historia temprana del término inteligencia artificial." },
        { id: "deepblue", title: "IBM Deep Blue", organization: "IBM", url: "https://www.ibm.com/history/deep-blue", usedFor: "El encuentro con Kasparov en 1997 y el papel de Deep Blue en la historia de la IA." },
        { id: "alexnet", title: "ImageNet Classification with Deep Convolutional Neural Networks", organization: "Krizhevsky, Sutskever & Hinton · 2012", url: "https://proceedings.neurips.cc/paper/2012/hash/c399862d3b9d6b76c8436e924a68c45b-Abstract.html", usedFor: "El hito de 2012 en aprendizaje profundo, ImageNet y entrenamiento con GPU." },
        { id: "transformer", title: "Attention Is All You Need", organization: "Vaswani et al. · 2017", url: "https://arxiv.org/abs/1706.03762", usedFor: "La arquitectura Transformer y su papel en modelos de lenguaje posteriores." },
        { id: "iea", title: "Energy and AI", organization: "International Energy Agency", url: "https://www.iea.org/reports/energy-and-ai", usedFor: "Electricidad de centros de datos, infraestructura, energía y contexto ambiental." },
      ],
      metadataTitle: "Cómo gestionamos las afirmaciones",
      metadata:
        "El contenido de producción debe registrar: afirmación · fuente · fecha de publicación · última revisión · curador · copia en inglés · copia en español.",
      reviewed:
        "El módulo de estado actual está separado para que indicadores y datos energéticos puedan actualizarse sin rediseñar la exhibición.",
      linkNote:
        "En modo quiosco, estos enlaces pueden representarse mediante códigos QR o una página de recursos para llevar, en lugar de abrir un navegador sin restricciones.",
    },
  },
});

export function getAIExhibitBatch5Copy(language = "en") {
  return AI_EXHIBIT_BATCH5_COPY[language] || AI_EXHIBIT_BATCH5_COPY.en;
}
